import React, { createContext, useReducer, useContext, useEffect, useState, } from 'react';
import { clearAllPersistedState, usePersistedState } from './UsePersistedState';
import HamQuest from '../api/HamQuestGiver';
import { QuestRoute } from '../api/QuestRoutes';
import { defaultWidgets } from '../widgets/WidgetIndex';
import { Enum, decodeJWT, generateId } from '../utility/functions';
import KwestGiver from 'kwest-giver';
import ModalFactory from '../Modal';

const initialState = {
    dark_theme: false,
    active_user: {
        isLoggedIn: false,
        accessToken: null,
        refreshToken: null,
        roles: [],
    },
    kioskServicesUsage: [],
    locations: [],
    allLocations: [],
    quick_menu: [],
    dashboards: [
        {
            id: generateId(),
            label: 'Dashboard 1',
            layout: {},
            widgets: defaultWidgets
        },
    ],
    help: {},
    states: null,
    subscriptions: [],
    mobileView: window.innerWidth < 1200,
    rowedTabs: false,
    version: '1.0.0'
}

export const GlobalContext = createContext(initialState);

const StateProvider = ({ children, initialValues = initialState }) => {
    // clearAllPersistedState();

    const [persistedState, setPersistedState] = usePersistedState('global', initialValues);
    const [state, dispatch] = useReducer(Reducer, persistedState);

    useEffect(() => {
        setPersistedState(state);
    }, [setPersistedState, state]);

    useEffect(() => {
        let timeoutId = null;

        const handleStorageChange = event => {
            if (event.key.match('global')) {
                if (timeoutId) {
                    clearTimeout(timeoutId);
                }

                timeoutId = setTimeout(() => {
                    const updatedState = JSON.parse(event.newValue);
                    dispatch({ type: ActionType.REHYDRATE_STATE, payload: updatedState });
                }, 200);
            }
        };

        window.addEventListener('storage', handleStorageChange);

        return () => {
            window.removeEventListener('storage', handleStorageChange);
        };
    }, []);

    return (
        <GlobalContext.Provider value={{ state, dispatch }}>
            {children}
        </GlobalContext.Provider>
    );
}

const QUEST_KEYS = 'HSQuestKeys';

const ActionType = new Enum([
    'REHYDRATE_STATE',
    'TOGGLE_THEME',
    'SET_LOGGED_IN',
    'UPDATE_DASHBOARD',
    'SET_DASHBOARD',
    'SET_ACCESS_TOKEN',
    'CLEAR_TOKENS',
    'SET_FEATURES',
    'SET_STATES',
    'SET_SUBSCRIPTIONS',
    'SET_LOCATIONS',
    'SET_LOCATIONS_KSUSAGE',
    'SET_TERMINALS',
    'SET_USER_DETAILS',
    'SET_TOOLTIPS',
    'SET_PAGE_INFO',
    'SET_MENU',
    'TOGGLE_ROWTAB',
    'SET_QUICK_MENU',
    'LOG_OUT',
    'SET_SEASONAL',
    'TOGGLE_SEASONAL',
]);


export function useGlobalState() {
    const { state, dispatch } = useContext(GlobalContext);

    const [functions] = useState({
        getQuestKeys: () => {
            try {
                const keys = window.localStorage.getItem(QUEST_KEYS);
                const parsedKeys = JSON.parse(keys);
                return parsedKeys;
            } catch {
                console.warn('No Auth keys...')
                return {};
            }
        },

        getCacheQuestKeys: async () => {
            if (!('caches' in window)) {
                console.error('Cache API not supported');
                return functions.getQuestKeys()
            }

            try {
                const cache = await caches.open(QUEST_KEYS);
                const response = await cache.match('/' + QUEST_KEYS);

                if (!response) {
                    throw new Error('No refresh token found');
                }
                const tokens = await response.json();
                console.log('Retrieved successfully.');
                return tokens;
            } catch (error) {
                console.error('Failed to retrieve the refresh token:', error);
            }
        },

        setCacheQuestKeys: async (refresh, access) => {
            try {
                if (!('caches' in window)) {
                    throw new Error('Cache API not supported');
                }

                const cache = await caches.open(QUEST_KEYS);
                const payload = JSON.stringify({ access, refresh });

                const response = new Response(payload, { headers: { 'Content-Type': 'application/json' } });

                await cache.put('/' + QUEST_KEYS, response);
                console.log("Cache successful");
            } catch (error) {
                console.error('Failed to store the refresh token:', error);
            }
        },

        setQuestKeys: (refresh, access) => {
            const state = {
                access: access,
                refresh: refresh
            }
            window.localStorage.setItem(QUEST_KEYS, JSON.stringify(state));

            functions.setCacheQuestKeys(refresh, access);
        },

        updateState: (updatedState) => dispatch({ type: ActionType.REHYDRATE_STATE, payload: updatedState }),

        updateLogIn: (res) => {
            const { accessToken, refreshToken } = res
            const decoded = decodeJWT(accessToken);

            dispatch({ type: ActionType.SET_ACCESS_TOKEN, payload: res });
            dispatch({ type: ActionType.SET_LOGGED_IN, payload: true });
            HamQuest.setLifecycle(decoded.exp * 1000);
            functions.setQuestKeys(refreshToken, accessToken);
        },

        updateAccessToken: () => {
            return new Promise((resolve, reject) => {
                try {
                    functions.getCacheQuestKeys()
                        .then(res => {
                            if (!res || !res.access || !res.refresh) {
                                if (state.active_user.isLoggedIn) functions.logOut();
                                return;
                            }

                            KwestGiver.put(process.env.REACT_APP_API_URL + QuestRoute.refreshToken, {
                                accessToken: res.access,
                                refreshToken: res.refresh,
                            })
                                .then(res => {
                                    functions.updateLogIn(res.resultData)
                                    resolve(res.resultData);
                                })
                                .catch(er => {
                                    console.error(er)
                                    ModalFactory.show({
                                        title: 'Logged Out',
                                        body: 'You have been logged out',
                                        type: 'warning'
                                    })
                                    functions.logOut();
                                })
                        });

                } catch (err) {
                    console.error('Failed to refresh token.')
                    reject(err)
                    throw err;
                }
            })
        },

        toggleTheme: () => dispatch({ type: ActionType.TOGGLE_THEME }),

        toggleRowTab: () => dispatch({ type: ActionType.TOGGLE_ROWTAB }),

        setDashboard: (dash) => dispatch({ type: ActionType.UPDATE_DASHBOARD, payload: { ...dash } }),

        updateQuickMenu: (route, label, currentState) => {
            let newQuickMenu = [...currentState];
            if (newQuickMenu.find(e => e.route === route)) {
                newQuickMenu = newQuickMenu.filter(e => e.route !== route);
            } else {
                newQuickMenu.push({ route: route, label: label });
            }

            dispatch({ type: ActionType.SET_QUICK_MENU, payload: newQuickMenu });
        },

        logIn: (credentials) => {
            return new Promise((resolve, reject) => {
                try {
                    HamQuest.post(QuestRoute.login, credentials)
                        .then(res => {
                            if (!credentials.rememberMe) delete res.refreshToken;
                            functions.updateLogIn(res);

                            if (res.tosAccepted) {
                                functions.getSiteData();
                                functions.getUserData();
                            }

                            resolve(true)
                        })
                        .catch(err => {
                            reject(err)
                        })
                } catch (err) {
                    console.error('Failed to Log In.')
                    reject(err)
                    throw err;
                }
            })
        },

        logOut: () => {
            dispatch({ type: ActionType.LOG_OUT, });
            functions.setQuestKeys(null, null);
            HamQuest.emptyQueue();
        },

        acceptTos: (override = true) => {
            return new Promise((resolve, reject) => {
                try {
                    HamQuest.put(QuestRoute.acceptTos, { accepted: override })
                        .then(res => {
                            functions.updateLogIn(res)
                            functions.getSiteData();
                            functions.getUserData();
                            resolve(true)
                        })
                        .catch(err => {
                            reject(err)
                        })
                } catch (err) {
                    console.error('Failed to Accept ToS.')
                    reject(err)
                    throw err;
                }
            })
        },

        getTooltips: () => {
            try {
                HamQuest.get(QuestRoute.tooltips)
                    .then(res => {
                        dispatch({
                            type: ActionType.SET_TOOLTIPS,
                            payload: res
                        });
                    })
                    .catch(err => {
                        console.error(err)
                    })
            } catch (err) {
                throw err;
            }
        },

        getPageInfo: () => {
            try {
                HamQuest.get(QuestRoute.pageInfo)
                    .then(res => {
                        dispatch({
                            type: ActionType.SET_PAGE_INFO,
                            payload: res
                        });
                    })
                    .catch(err => {
                        console.error(err)
                    })
            } catch (err) {
                throw err;
            }
        },

        getFeatures: () => {
            try {
                HamQuest.get(QuestRoute.features)
                    .then(res => { dispatch({ type: ActionType.SET_FEATURES, payload: res }); })
                    .catch(ex => console.error(ex))
            } catch (err) {
                throw err;
            }
        },

        getSiteData: () => {
            try {
                functions.getFeatures();

                HamQuest.get(QuestRoute.subscriptions)
                    .then(res => { dispatch({ type: ActionType.SET_SUBSCRIPTIONS, payload: res }); })
                    .catch(ex => console.error(ex))
                
                HamQuest.get(QuestRoute.states)
                    .then(res => {
                        dispatch({
                            type: ActionType.SET_STATES,
                            payload: res
                        });
                    })
                    .catch(ex => console.error(ex))
                
                HamQuest.get(QuestRoute.locations.ksEndpointUsage)
                .then(res => {
                    dispatch({
                        type: ActionType.SET_LOCATIONS_KSUSAGE,
                        payload: res
                    });
                })
                .catch(ex => console.error(ex))                   

                HamQuest.get(QuestRoute.terminals.ids)
                    .then(res => {
                        dispatch({
                            type: ActionType.SET_TERMINALS,
                            payload: res.map(String)
                        });
                    })
                    .catch(ex => console.error(ex))

                functions.getTooltips();
                functions.getPageInfo();
            } catch (err) {
                throw err;
            }
        },
        
        getUserData: () => {
            try {
                HamQuest.get(QuestRoute.roles)
                    .then(res => {
                        dispatch({
                            type: ActionType.SET_USER_DETAILS,
                            payload: res
                        });
                    })

                HamQuest.get(QuestRoute.settings.dashboard)
                    .then(res => {
                        dispatch({
                            type: ActionType.SET_DASHBOARD,
                            payload: JSON.parse(res)
                        });
                    })
                    .catch(ex => {
                        console.warn(ex)
                        dispatch({
                            type: ActionType.SET_DASHBOARD,
                            payload: [{
                                id: generateId(),
                                label: 'Dashboard 1',
                                layout: {},
                                widgets: defaultWidgets
                            }]
                        });
                    })

                HamQuest.get(QuestRoute.locations.labels)
                    .then(res => {
                        dispatch({
                            type: ActionType.SET_LOCATIONS,
                            payload: res
                        });
                    })
                    .catch(ex => console.error(ex))

                // HamQuest.get(QuestRoute.menu)
                //     .then(res => {
                //         dispatch({
                //             type: ActionType.SET_MENU,
                //             payload: res
                //         });
                //     })

            } catch (err) {
                throw err;
            }
        },

        updateUnauthorized: () => {
            HamQuest.setUnauthorized((url, config) => {
                return new Promise((resolve, reject) => {
                    functions
                        .updateAccessToken()
                        .then(() => HamQuest.fetchQuest(url, config)
                            .then(res => resolve(res))
                            .catch(ex => reject(ex)))
                })
            })
        },

        toggleSeasonal: (override) => dispatch({
            type: ActionType.TOGGLE_SEASONAL,
            payload: override
        }),
    });

    return {
        ...functions,
        state,
    };
}

export default StateProvider;

const Reducer = (state = initialState, action) => {
    let newState = structuredClone(state);
    switch (action.type) {
        case ActionType.REHYDRATE_STATE:
            console.log('State Updated Elsewhere.')
            newState = structuredClone(action.payload);
            break;
        case ActionType.TOGGLE_THEME:
            newState.dark_theme = !newState.dark_theme;
            break;
        case ActionType.SET_LOGGED_IN:
            newState.active_user.isLoggedIn = action.payload;
            break;
        case ActionType.SET_ACCESS_TOKEN:
            newState.active_user = { ...newState.active_user, ...action.payload }
            break;

        case ActionType.CLEAR_TOKENS:
            newState.active_user.accessToken = null;
            newState.active_user.refreshToken = null;
            break;
        case ActionType.LOG_OUT:
            newState.active_user = {
                isLoggedIn: false,
                roles: []
            };
            newState.locations = [];
            newState.allLocations = [];
            newState.terminals = [];
            newState.states = [];
            break;
        case ActionType.SET_STATES:
            newState.states = action.payload;
            break;
        case ActionType.SET_LOCATIONS:
            newState.locations = action.payload.filter(e => e.visible !== false);
            newState.allLocations = action.payload;
            break;
        case ActionType.SET_LOCATIONS_KSUSAGE:
            newState.kioskServicesUsage = action.payload;
        case ActionType.SET_TERMINALS:
            newState.terminals = action.payload;
            break;
        case ActionType.SET_USER_DETAILS:
            newState.active_user = { ...newState.active_user, ...action.payload };
            break;
        case ActionType.SET_TOOLTIPS:
            newState.help = action.payload;
            break;
        case ActionType.SET_PAGE_INFO:
            newState.pageInfo = action.payload;
            break;
        case ActionType.SET_MENU:
            newState.active_user.menu = action.payload;
            break;
        case ActionType.SET_QUICK_MENU:
            newState.quick_menu = action.payload;
            break;
        case ActionType.TOGGLE_ROWTAB:
            newState.rowedTabs = !newState.rowedTabs;
            break;

        case ActionType.SET_DASHBOARD:
            newState.dashboards = action.payload;
            break;
        case ActionType.UPDATE_DASHBOARD:
            if (!newState.dashboards[action.payload.index]) {
                newState.dashboards[action.payload.index] = {};
                newState.dashboards[action.payload.index].id = generateId();
                newState.dashboards[action.payload.index].label = 'Dashboard ' + (action.payload.index + 1);
            }
            if (action.payload.layout) {
                newState.dashboards[action.payload.index].layout = action.payload.layout;
            }
            if (action.payload.label) {
                newState.dashboards[action.payload.index].label = action.payload.label;
            }
            if (action.payload.widgets) {
                newState.dashboards[action.payload.index].widgets = action.payload.widgets;
            }
            if (action.payload.remove) {
                newState.dashboards = newState.dashboards.filter((e, i) => i !== action.payload.index);
                newState.dashboards.forEach((e, i) => {
                    if (e.label.match(/Dashboard \d+/)) {
                        e.label = 'Dashboard ' + (i + 1)
                    }
                })
            }
            if(action.payload.reset) {
                //set new Id to trigger re-rendering for default layout
                newState.dashboards[action.payload.index].id = generateId();
                newState.dashboards[action.payload.index].layout = {};
                newState.dashboards[action.payload.index].widgets = defaultWidgets;
            }
            break;

        case ActionType.TOGGLE_SEASONAL:
            newState.seasonal = action.payload ?? !newState.seasonal;
            break
        case ActionType.SET_FEATURES:
            newState.features = action.payload;
            break;
        case ActionType.SET_SUBSCRIPTIONS:
            newState.subscriptions = action.payload;
            break;
        default:
            console.error(`Unknown action type: ${action.type}`);
            throw new Error();
    }
    return newState;
};
